'use client';

import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { ISbStoryData } from 'storyblok-js-client';

import { STORAGE_KEYS } from '@monoimin/constants';
import { Popup } from '@monoimin/molecules/Popup';
import { getCountryCookie } from '@monoimin/utils/api/getCountryCookie';
import { trackAmplitudeEvent } from '@monoimin/analytics';
import { C2APopupStoryblok, CommonMessages, Locales } from '@monoimin/sb-types';
import { setSessionStorageItem } from 'libs/utils/src/lib/storage/sessionStorage';

import getAnalyticsData from '../../utils/getAnalyticsData';

import { PopupContent } from './PopupContent';

export interface FactsheetPopupProps {
  stories: ISbStoryData<C2APopupStoryblok>[];
  locale: Locales;
  messages: CommonMessages;
}

export default function FactsheetPopup({
  stories,
  locale,
  messages,
}: FactsheetPopupProps) {
  const [country, setCountry] = useState<string | null>(null);
  const [selectedContent, setSelectedContent] =
    useState<C2APopupStoryblok | null>(null);
  const [storyUID, setStoryUID] = useState<string>('');
  const [analyticsTracked, setAnalyticsTracked] = useState(false);

  useEffect(() => {
    // Get country from cookie after component mounts (client-side only)
    const userCountry = getCountryCookie();
    setCountry(userCountry);

    const matchingStory = stories.find((story) =>
      story.content.country?.includes(userCountry ?? '')
    );
    const matchingContent = matchingStory?.content;

    setSelectedContent(matchingContent || null);
    setStoryUID(matchingStory?.uuid ?? '');

    if (matchingContent) {
      setSessionStorageItem('isCountySpecificBanner', 'true');
    }
  }, []);

  const blockName = selectedContent?.customBlockName
    ? `CTA Popup ${country} ${selectedContent.customBlockName}`
    : `CTA Popup ${country}`;

  // use Intersection Observer to track when popup is visible
  useEffect(() => {
    if (!selectedContent || !storyUID || analyticsTracked) return;

    // Create the observer
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          const analyticsData = getAnalyticsData(
            selectedContent,
            { storyUID, locale, messages },
            'view'
          );
          trackAmplitudeEvent({
            name: 'Popup',
            deviceId: '',
            userId: null,
            properties: {
              ...analyticsData,
              block_name: blockName,
              block_variant: 'Popup',
            },
          });
          setAnalyticsTracked(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    // Set up a mutation observer to detect when the popup is added to the DOM
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const popupElement = document.querySelector('.factsheet-popup');
          if (popupElement) {
            observer.observe(popupElement);
            mutationObserver.disconnect();
            break;
          }
        }
      }
    });

    // Start observing the document body for changes
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [
    selectedContent,
    storyUID,
    blockName,
    analyticsTracked,
    locale,
    messages,
  ]);

  if (!selectedContent) return null;

  const clickAnalyticsData = getAnalyticsData(
    selectedContent,
    { storyUID, locale, messages },
    'click'
  );
  return (
    <Popup
      dataTestId='factsheet-popup'
      closedStorageKey={STORAGE_KEYS.FACTSHEET_POPUP.CLOSED}
      preventOpeningStorageKey={STORAGE_KEYS.FACTSHEET_POPUP.SENT}
      classNames={{
        container: clsx(
          'popup factsheet-popup shadow-popup border-t bg-blueGrey border-black-200 transition ease-in group',
          'data-[closed]:opacity-0',
          'data-[enter]:duration-300 data-[enter]:data-[closed]:translate-y-full',
          'data-[leave]:duration-200 data-[leave]:data-[closed]:translate-y-full'
        ),
        closeButtonIcon: 'w-6 lg:w-7 h-6 lg:h-7',
        closeButton: 'lg:translate-y-1/2 lg:bottom-1/2 top-5 right-4',
      }}
      onClose={() => {
        trackAmplitudeEvent({
          name: 'Popup',
          deviceId: '',
          userId: null,
          properties: {
            ...clickAnalyticsData,
            block_name: blockName,
            block_variant: 'Popup',
            button_type: 'close-button',
          },
        });
      }}
    >
      <PopupContent
        content={selectedContent}
        onClick={() => {
          trackAmplitudeEvent({
            name: 'Popup',
            deviceId: '',
            userId: null,
            properties: {
              ...clickAnalyticsData,
              block_name: blockName,
              block_variant: 'Popup',
              button_type: 'send-button',
            },
          });
        }}
      />
    </Popup>
  );
}
